/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR307: Unified Deal Cards Data Hook
 * 
 * Single source of truth for deal card data across all admin surfaces:
 * - Deal Cards view
 * - Live Preview
 * - Layout Studio preview
 * 
 * Provides explicit Live/Sample toggle with publish state metadata.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toUiDeals, type UIDeal, type RawDealInput } from "@/lib/deals/toUiDeal";
import { mergeCanonicalDeal } from "@/lib/deals/mergeCanonicalDeal";
import { hydrateFlyerArtifactsBatch } from "@/lib/deals/fetchFlyerSnippetForDeal";

// ================================================================
// TYPES
// ================================================================

export type DealCardsDataSource = "live" | "sample";

export interface PublishState {
  environment: string;
  activeSnapshotId: string | null;
  lastPublishedAt: string | null;
  lastPublishedRowCount: number;
}

export interface DealCardsDataMeta {
  source: DealCardsDataSource;
  publishState: PublishState | null;
  dealCount: number;
  lastRefreshed: string;
  error: string | null;
}

/** CR306: Sort option IDs matching Layout Studio */
export type DealSortOption = "newest" | "best_value" | "expiring_soon" | "merchant_az" | "category_az";

export interface UseDealCardsDataOptions {
  /** Default source on first load */
  defaultSource?: DealCardsDataSource;
  /** Maximum deals to fetch */
  limit?: number;
  /** Optional deal type filter */
  dealTypeFilter?: string | null;
  /** Persist source selection to localStorage */
  persistSource?: boolean;
  /** CR306: Sort option - when 'best_value', uses SQL-based sorting with status prioritization */
  sortOption?: DealSortOption;
}

export interface UseDealCardsDataResult {
  // Data
  deals: UIDeal[];
  meta: DealCardsDataMeta;
  
  // Source state
  source: DealCardsDataSource;
  
  // Loading state
  loading: boolean;
  
  // Actions
  setSource: (source: DealCardsDataSource) => void;
  refresh: () => Promise<void>;
}

// ================================================================
// CONSTANTS
// ================================================================

const STORAGE_KEY = "ui.dealCards.dataSource";
const DEFAULT_LIMIT = 50;

// ================================================================
// HOOK
// ================================================================

export function useDealCardsData(
  options: UseDealCardsDataOptions = {}
): UseDealCardsDataResult {
  const {
    defaultSource = "live",
    limit = DEFAULT_LIMIT,
    dealTypeFilter = null,
    persistSource = true,
    sortOption = "newest",
  } = options;

  // Source state - defaults to "live"
  const [source, setSourceState] = useState<DealCardsDataSource>(defaultSource);
  
  // Data state
  const [deals, setDeals] = useState<UIDeal[]>([]);
  const [publishState, setPublishState] = useState<PublishState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<string>(new Date().toISOString());

  // Load persisted source on mount
  useEffect(() => {
    if (!persistSource) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === "live" || stored === "sample")) {
        setSourceState(stored as DealCardsDataSource);
      }
    } catch {
      // localStorage not available
    }
  }, [persistSource]);

  // Persist source changes
  const setSource = useCallback((newSource: DealCardsDataSource) => {
    setSourceState(newSource);
    if (persistSource) {
      try {
        localStorage.setItem(STORAGE_KEY, newSource);
      } catch {
        // localStorage not available
      }
    }
  }, [persistSource]);

  // Fetch publish state
  const fetchPublishState = useCallback(async (): Promise<PublishState | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from("deals_publish_state")
        .select("environment, active_snapshot_id, last_published_at, last_published_row_count")
        .eq("environment", "publish")
        .maybeSingle();

      if (fetchError) {
        console.error("[useDealCardsData] Error fetching publish state:", fetchError);
        return null;
      }

      return data ? {
        environment: data.environment,
        activeSnapshotId: data.active_snapshot_id,
        lastPublishedAt: data.last_published_at,
        lastPublishedRowCount: data.last_published_row_count,
      } : null;
    } catch (err) {
      console.error("[useDealCardsData] Exception fetching publish state:", err);
      return null;
    }
  }, []);

  // Fetch deals from LIVE view (v_deals_live_enriched — CR415)
  const fetchLiveDeals = useCallback(async (): Promise<UIDeal[]> => {
    console.log("[CR415][useDealCardsData] Fetching LIVE deals from v_deals_live_enriched");
    
    // CR415: Use enriched view that joins issuer fields from merchants table
    // Select only base columns for TS; access issuer fields via cast on response
    const query = supabase
      .from("v_deals_live_enriched" as "v_deals_live")
      .select("*")
      .order("last_published_at", { ascending: false })
      .limit(limit);

    // Apply deal type filter if specified
    if (dealTypeFilter && dealTypeFilter !== "ALL") {
      // Filter by deal_type from canonical_deal_json
      // Note: We filter client-side since JSON filtering in views is complex
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      console.error("[useDealCardsData] Live query error:", fetchError);
      throw new Error(fetchError.message);
    }

    // Map to RawDealInput format for toUiDeals
    // CR434: Use mergeCanonicalDeal to prevent canonical JSON from clobbering row.id
    const rawDeals: RawDealInput[] = (data || []).map(row => {
      const merged = mergeCanonicalDeal(row);
      return {
        ...merged,
        id: merged.id as string ?? "missing-id",
        dedupe_key: row.dedupe_key,
        canonical_deal_json: merged.canonical_deal_json as Record<string, unknown> ?? {},
        computed_value_cad: row.computed_value_cad,
        effective_percent_back: row.effective_percent_back,
        valid_from: row.valid_from,
        valid_until: row.valid_until,
        visibility_status: row.visibility_status,
        // CR316D: Value fields from enriched view
        qualifying_spend_cad: (row as any).qualifying_spend_cad ?? null,
        discount_amount_cad: (row as any).discount_amount_cad ?? null,
        discount_percent: (row as any).discount_percent ?? null,
        // CR316D: Date aliases
        start_date: (row as any).start_date ?? null,
        end_date: (row as any).end_date ?? null,
        // CR415: Issuer fields from enriched view
        issuer_display_name: (row as any).issuer_display_name ?? null,
        issuer_logo_url: (row as any).issuer_logo_url ?? null,
        issuer_merchant_key: (row as any).issuer_merchant_key ?? null,
      } as RawDealInput;
    });

    // Apply client-side deal type filter
    let filtered = rawDeals;
    if (dealTypeFilter && dealTypeFilter !== "ALL") {
      filtered = rawDeals.filter(d => {
        const dt = (d.deal_type || d.deal_category || "").toString().toUpperCase();
        return dt === dealTypeFilter.toUpperCase();
      });
    }

    const uiDeals = toUiDeals(filtered, "live");

    // CR447: Batch hydrate flyer snippets (no N+1)
    const hydrated = await hydrateFlyerArtifactsBatch(uiDeals);
    return hydrated as UIDeal[];
  }, [limit, dealTypeFilter]);

  // Fetch deals from SAMPLE view (v_layout_studio_sample_deals or sortable view for best_value)
  const fetchSampleDeals = useCallback(async (): Promise<UIDeal[]> => {
    // CR306: Use sortable view when best_value sort is requested
    const useSortableView = sortOption === "best_value";
    
    console.log(`[CR306][useDealCardsData] Fetching SAMPLE deals, sortOption=${sortOption}, useSortableView=${useSortableView}`);
    
    // CR306: Define row type for both views (sortable has extra columns but we only use common ones)
    type SampleDealRow = {
      id: string;
      canonical_deal_json: Record<string, unknown> | null;
      deal_type: string | null;
      source_type: string | null;
      computed_value_cad: number | null;
      effective_percent_back: number | null;
      valid_from: string | null;
      valid_until: string | null;
    };
    
    let data: SampleDealRow[] | null = null;
    let fetchError: Error | null = null;
    
    if (useSortableView) {
      // CR306: Query sortable view with status-prioritized sorting
      const result = await (supabase
        .from("v_layout_studio_sample_deals_sortable" as any)
        .select("id, canonical_deal_json, deal_type, source_type, computed_value_cad, effective_percent_back, valid_from, valid_until")
        // CR306: Active/Upcoming first (expired_rank=0), then by % value desc, alpha tie-break, id stable
        .order("expired_rank", { ascending: true })
        .order("percent_sort", { ascending: false })
        .order("merchant_name_sort", { ascending: true })
        .order("id", { ascending: true })
        .limit(limit) as any);
      
      if (result.error) {
        fetchError = new Error(result.error.message);
      } else {
        data = result.data as unknown as SampleDealRow[];
      }
      
      // Apply deal type filter (must do after query since we can't chain .eq on the above)
      if (dealTypeFilter && dealTypeFilter !== "ALL" && data) {
        data = data.filter(row => 
          row.deal_type?.toUpperCase() === dealTypeFilter.toUpperCase()
        );
      }
    } else {
      // Standard view with default sorting
      let query = supabase
        .from("v_layout_studio_sample_deals")
        .select("id, canonical_deal_json, deal_type, source_type, computed_value_cad, effective_percent_back, valid_from, valid_until")
        .order("id", { ascending: false })
        .limit(limit);

      // Apply deal type filter if specified
      if (dealTypeFilter && dealTypeFilter !== "ALL") {
        query = query.eq("deal_type", dealTypeFilter.toUpperCase());
      }

      const result = await query;
      if (result.error) {
        fetchError = new Error(result.error.message);
      } else {
        data = result.data as SampleDealRow[];
      }
    }

    if (fetchError) {
      console.error("[useDealCardsData] Sample query error:", fetchError);
      throw fetchError;
    }

    // Map to RawDealInput format for toUiDeals
    const rawDeals: RawDealInput[] = (data || []).map(row => {
      const json = (typeof row.canonical_deal_json === 'object' && row.canonical_deal_json !== null
        ? row.canonical_deal_json
        : {}) as Record<string, unknown>;
      // CR434: Use mergeCanonicalDeal to prevent canonical JSON from clobbering row.id
      const merged = mergeCanonicalDeal(row);
      return {
        ...merged,
        canonical_deal_json: json,
        computed_value_cad: row.computed_value_cad,
        effective_percent_back: row.effective_percent_back,
        valid_from: row.valid_from,
        valid_until: row.valid_until,
        deal_type: row.deal_type,
        source_type: row.source_type,
        // Flatten key fields for toUiDeal
        merchant_name: json.merchant_name as string,
        promo_merchant_name: json.promo_merchant_name as string,
        reward_brand_name: json.reward_brand_name as string,
        headline_mechanic: json.headline_mechanic as string,
        deal_category: json.deal_category as string,
        // CR434: force id last — never let canonical clobber
        id: String(merged.id ?? row.id ?? "missing-id"),
      };
    });

    const uiDeals = toUiDeals(rawDeals, "staging");

    // CR447: Batch hydrate flyer snippets for sample deals too
    const hydrated = await hydrateFlyerArtifactsBatch(uiDeals);
    return hydrated as UIDeal[];
  }, [limit, dealTypeFilter, sortOption]);

  // Main fetch function
  const fetchDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Always fetch publish state
      const ps = await fetchPublishState();
      setPublishState(ps);

      // Fetch deals based on source
      const fetchedDeals = source === "live" 
        ? await fetchLiveDeals()
        : await fetchSampleDeals();

      setDeals(fetchedDeals);
      setLastRefreshed(new Date().toISOString());
      
      console.log(`[CR307][useDealCardsData] Fetched ${fetchedDeals.length} deals from ${source}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("[useDealCardsData] Fetch error:", message);
      setError(message);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [source, fetchLiveDeals, fetchSampleDeals, fetchPublishState]);

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchDeals();
  }, [fetchDeals]);

  // Auto-fetch on source change
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Build meta object
  const meta = useMemo<DealCardsDataMeta>(() => ({
    source,
    publishState,
    dealCount: deals.length,
    lastRefreshed,
    error,
  }), [source, publishState, deals.length, lastRefreshed, error]);

  return {
    deals,
    meta,
    source,
    loading,
    setSource,
    refresh,
  };
}

export default useDealCardsData;
